import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import {
  Search,
  Blocks,
  Shield,
  DollarSign,
  Clock,
  CheckCircle,
  Sparkles,
  Copy,
  ArrowLeft,
  ArrowRight,
  Save,
  Loader2,
  FileText
} from "lucide-react";
import { copyToClipboard } from "../../utils/clipboard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PHASES = [
  { id: "marketResearch", name: "Market Research", icon: Search, color: "#3b82f6" },
  { id: "offerArchitecture", name: "Offer Architecture", icon: Blocks, color: "#8b5cf6" },
  { id: "riskReversal", name: "Risk Reversal", icon: Shield, color: "#f59e0b" },
  { id: "pricingPosition", name: "Pricing Position", icon: DollarSign, color: "#10b981" },
  { id: "urgencyLayer", name: "Urgency Layer", icon: Clock, color: "#ef4444" },
  { id: "finalOffer", name: "Final Offer", icon: CheckCircle, color: "#00d4aa" }
];

const GUARANTEE_TYPES = [
  { value: "performance", label: "Performance Guarantee" },
  { value: "satisfaction", label: "Satisfaction Guarantee" },
  { value: "hybrid", label: "Hybrid Guarantee" },
  { value: "conditional", label: "Conditional Guarantee" }
];

function OfferBuilder() {
  const { offerId } = useParams();
  const navigate = useNavigate();
  
  const [currentPhase, setCurrentPhase] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isResearching, setIsResearching] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [showOutput, setShowOutput] = useState(true);
  
  const [offer, setOffer] = useState({
    offer_id: null,
    clientName: "",
    industry: "",
    status: "draft",
    phases: {
      marketResearch: { inputs: {}, aiOutput: "", researchData: {} },
      offerArchitecture: { inputs: {}, aiOutput: "" },
      riskReversal: { inputs: {}, aiOutput: "" },
      pricingPosition: { inputs: {}, aiOutput: "" },
      urgencyLayer: { inputs: {}, aiOutput: "" },
      finalOffer: { inputs: {}, aiOutput: "" }
    }
  });

  // Load existing offer if editing
  useEffect(() => {
    if (offerId) {
      loadOffer(offerId);
    }
  }, [offerId]);

  // Auto-save every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (offer.offer_id) {
        saveOffer(true);
      }
    }, 30000);
    
    return () => clearInterval(interval);
  }, [offer]);

  const loadOffer = async (id) => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/offers/${id}`);
      if (response.data.offer) {
        setOffer(response.data.offer);
      }
    } catch (error) {
      console.error("Load offer error:", error);
      toast.error("Failed to load offer");
    } finally {
      setIsLoading(false);
    }
  };

  const saveOffer = useCallback(async (silent = false) => {
    if (isSaving) return;
    
    setIsSaving(true);
    try {
      if (offer.offer_id) {
        // Update existing offer
        await axios.put(`${API}/offers/${offer.offer_id}`, {
          clientName: offer.clientName,
          industry: offer.industry,
          status: offer.status,
          phases: offer.phases
        });
      } else {
        // Create new offer
        const response = await axios.post(`${API}/offers`, {
          clientName: offer.clientName || "Untitled Offer",
          industry: offer.industry,
          status: "draft",
          phases: offer.phases
        });
        
        if (response.data.offer_id) {
          setOffer(prev => ({ ...prev, offer_id: response.data.offer_id }));
          // Update URL without reload
          window.history.replaceState(null, "", `/offers/build/${response.data.offer_id}`);
        }
      }
      
      setLastSaved(new Date());
      if (!silent) {
        toast.success("Offer saved");
      }
    } catch (error) {
      console.error("Save offer error:", error);
      if (!silent) {
        toast.error("Failed to save offer");
      }
    } finally {
      setIsSaving(false);
    }
  }, [offer, isSaving]);

  const updatePhaseInput = (phase, field, value) => {
    setOffer(prev => ({
      ...prev,
      phases: {
        ...prev.phases,
        [phase]: {
          ...prev.phases[phase],
          inputs: {
            ...prev.phases[phase].inputs,
            [field]: value
          }
        }
      }
    }));
  };

  const generateInsight = async () => {
    const phase = PHASES[currentPhase].id;
    const phaseData = offer.phases[phase].inputs;
    
    // Add client info to market research phase
    if (phase === "marketResearch") {
      phaseData.clientName = offer.clientName;
      phaseData.industry = offer.industry;
    }
    
    setIsGenerating(true);
    try {
      const response = await axios.post(`${API}/offers/generate-insight`, {
        phase,
        phaseData,
        allPhaseData: offer.phases
      });
      
      if (response.data.insight) {
        setOffer(prev => ({
          ...prev,
          phases: {
            ...prev.phases,
            [phase]: {
              ...prev.phases[phase],
              aiOutput: response.data.insight
            }
          }
        }));
        toast.success("Insights generated!");
      }
    } catch (error) {
      console.error("Generate insight error:", error);
      toast.error(error.response?.data?.detail || "Failed to generate insights");
    } finally {
      setIsGenerating(false);
    }
  };

  // Generate market research from basic details
  const generateResearch = async () => {
    const inputs = offer.phases.marketResearch.inputs;
    
    // Validate basic fields
    if (!offer.clientName || !offer.industry || !inputs.audienceSegment) {
      toast.error("Please fill in Client Name, Industry, and Audience Segment first");
      return;
    }
    
    setIsResearching(true);
    try {
      const response = await axios.post(`${API}/offers/generate-insight`, {
        phase: "marketResearch",
        phaseData: {
          clientName: offer.clientName,
          industry: offer.industry,
          audienceSegment: inputs.audienceSegment,
          revenueRange: inputs.revenueRange || "Not specified",
          location: inputs.location || "Not specified"
        },
        allPhaseData: offer.phases
      });
      
      if (response.data.insight) {
        // Parse the AI response and extract research data
        const aiOutput = response.data.insight;
        
        // Extract sections from the AI output
        const extractSection = (text, sectionName) => {
          const regex = new RegExp(`## ${sectionName}[\\s\\S]*?(?=##|$)`, 'i');
          const match = text.match(regex);
          if (match) {
            return match[0].replace(new RegExp(`## ${sectionName}`, 'i'), '').trim();
          }
          return '';
        };
        
        const topPains = extractSection(aiOutput, 'TOP 3 PAIN POINTS');
        const failedSolutions = extractSection(aiOutput, 'FAILED SOLUTIONS');
        const customerLanguage = extractSection(aiOutput, 'CUSTOMER LANGUAGE');
        
        setOffer(prev => ({
          ...prev,
          phases: {
            ...prev.phases,
            marketResearch: {
              ...prev.phases.marketResearch,
              inputs: {
                ...prev.phases.marketResearch.inputs,
                topPains: topPains || prev.phases.marketResearch.inputs.topPains,
                failedSolutions: failedSolutions || prev.phases.marketResearch.inputs.failedSolutions,
                customerLanguage: customerLanguage || prev.phases.marketResearch.inputs.customerLanguage
              },
              aiOutput: aiOutput,
              researchData: { topPains, failedSolutions, customerLanguage }
            }
          }
        }));
        toast.success("Market research generated! Review and edit the findings below.");
      }
    } catch (error) {
      console.error("Generate research error:", error);
      toast.error(error.response?.data?.detail || "Failed to generate research");
    } finally {
      setIsResearching(false);
    }
  };

  const copyAllContent = () => {
    let content = `# OFFER: ${offer.clientName}\n\n`;
    
    PHASES.forEach(phase => {
      const phaseData = offer.phases[phase.id];
      content += `## ${phase.name}\n\n`;
      content += `### Inputs\n`;
      Object.entries(phaseData.inputs || {}).forEach(([key, value]) => {
        content += `- ${key}: ${value}\n`;
      });
      content += `\n### AI Output\n${phaseData.aiOutput || "Not generated"}\n\n`;
      content += "---\n\n";
    });
    
    copyToClipboard(content);
    toast.success("All content copied to clipboard");
  };

  // Map a completed offer into a CreateCampaign prefill payload and hand off.
  const createCampaignFromOffer = () => {
    const phases = offer.phases || {};
    const research = phases.marketResearch?.inputs || {};
    const architecture = phases.offerArchitecture?.inputs || {};
    const pricing = phases.pricingPosition?.inputs || {};
    const urgency = phases.urgencyLayer?.inputs || {};
    const risk = phases.riskReversal?.inputs || {};
    const finalAiOutput = phases.finalOffer?.aiOutput || '';

    const formData = {
      clientName: offer.clientName || '',
      campaignName: offer.clientName ? `${offer.clientName} Offer Campaign` : '',
      offer: architecture.coreOutcome || '',
      // Carry the full offer copy so the brief is grounded in the generated offer.
      goalDetails: finalAiOutput,
      whoIsThisPerson: research.audienceSegment || '',
      location: research.location || '',
      mainProblem: research.topPains || '',
      triedAndFailed: research.failedSolutions || '',
      exactLanguage: research.customerLanguage || '',
      bigPromise: architecture.coreOutcome || '',
      guarantee: risk.guaranteeConditions || '',
      deadline: urgency.deadline || '',
      otherRequirements: pricing.proposedPrice ? `Proposed price: ${pricing.proposedPrice}` : ''
    };

    navigate('/campaigns/create', {
      state: {
        prefill: {
          formData,
          platform: null,
          savedAds: [],
          currentAdIndex: 0,
          notice: 'Brief pre-filled from your offer — review and generate ads.'
        }
      }
    });
  };

  const renderPhaseForm = () => {
    const phase = PHASES[currentPhase].id;
    const inputs = offer.phases[phase]?.inputs || {};
    
    switch (phase) {
      case "marketResearch":
        return (
          <div className="space-y-6">
            {/* Basic Details Section */}
            <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <h3 className="text-sm font-semibold text-blue-400 mb-3 flex items-center gap-2">
                <Search className="w-4 h-4" />
                STEP 1: Basic Details
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-white/80 font-mono">Client/Project Name *</label>
                  <Input
                    value={offer.clientName}
                    onChange={(e) => setOffer(prev => ({ ...prev, clientName: e.target.value }))}
                    placeholder="e.g., Ace Plumbing"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-white/80 font-mono">Industry *</label>
                  <Input
                    value={offer.industry}
                    onChange={(e) => setOffer(prev => ({ ...prev, industry: e.target.value }))}
                    placeholder="e.g., Home Services"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-3 gap-4 mt-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-white/80 font-mono">Audience Segment *</label>
                  <Input
                    value={inputs.audienceSegment || ""}
                    onChange={(e) => updatePhaseInput(phase, "audienceSegment", e.target.value)}
                    placeholder="e.g., Homeowners aged 35-55"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-white/80 font-mono">Revenue Range</label>
                  <Input
                    value={inputs.revenueRange || ""}
                    onChange={(e) => updatePhaseInput(phase, "revenueRange", e.target.value)}
                    placeholder="e.g., $500K - $2M"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-white/80 font-mono">Location</label>
                  <Input
                    value={inputs.location || ""}
                    onChange={(e) => updatePhaseInput(phase, "location", e.target.value)}
                    placeholder="e.g., Perth, WA"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
              </div>
              
              {/* Research Button */}
              <div className="mt-4 pt-4 border-t border-blue-500/20">
                <Button
                  onClick={generateResearch}
                  disabled={isResearching || !offer.clientName || !offer.industry || !inputs.audienceSegment}
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold h-12"
                >
                  {isResearching ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Researching Audience...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      Research This Audience with AI
                    </>
                  )}
                </Button>
                <p className="text-xs text-blue-400/60 mt-2 text-center">
                  AI will research pain points, failed solutions, and customer language
                </p>
              </div>
            </div>
            
            {/* Research Results Section */}
            <div className={`p-4 rounded-lg border ${inputs.topPains ? 'bg-[#00d4aa]/10 border-[#00d4aa]/30' : 'bg-white/5 border-white/10'}`}>
              <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${inputs.topPains ? 'text-[#00d4aa]' : 'text-white/60'}`}>
                <FileText className="w-4 h-4" />
                STEP 2: Research Findings {inputs.topPains ? '(AI Generated - Edit as needed)' : '(Generated by AI)'}
              </h3>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-white/80 font-mono">Top 3 Pains</label>
                  <Textarea
                    value={inputs.topPains || ""}
                    onChange={(e) => updatePhaseInput(phase, "topPains", e.target.value)}
                    placeholder="Click 'Research This Audience' above to auto-fill, or enter manually..."
                    className="bg-white/5 border-white/10 text-white min-h-[100px]"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-white/80 font-mono">Failed Solutions</label>
                  <Textarea
                    value={inputs.failedSolutions || ""}
                    onChange={(e) => updatePhaseInput(phase, "failedSolutions", e.target.value)}
                    placeholder="What have they tried that didn't work?"
                    className="bg-white/5 border-white/10 text-white min-h-[100px]"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-white/80 font-mono">Customer Language</label>
                  <Textarea
                    value={inputs.customerLanguage || ""}
                    onChange={(e) => updatePhaseInput(phase, "customerLanguage", e.target.value)}
                    placeholder="Exact words and phrases they use..."
                    className="bg-white/5 border-white/10 text-white min-h-[100px]"
                  />
                </div>
              </div>
            </div>
          </div>
        );
        
      case "offerArchitecture":
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <label className="text-sm font-medium text-white/80 font-mono">Core Outcome</label>
              <Input
                value={inputs.coreOutcome || ""}
                onChange={(e) => updatePhaseInput(phase, "coreOutcome", e.target.value)}
                placeholder="One sentence describing the result they'll get"
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-white/80 font-mono">Deliverables</label>
              <Textarea
                value={inputs.deliverables || ""}
                onChange={(e) => updatePhaseInput(phase, "deliverables", e.target.value)}
                placeholder="List each deliverable on a new line:&#10;- Website redesign&#10;- 12 months of SEO&#10;- Monthly reporting"
                className="bg-white/5 border-white/10 text-white min-h-[150px]"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-white/80 font-mono">Value Stack (Bonuses)</label>
              <Textarea
                value={inputs.valueStack || ""}
                onChange={(e) => updatePhaseInput(phase, "valueStack", e.target.value)}
                placeholder="Bonuses and extras to increase perceived value:&#10;- Free audit ($500 value)&#10;- Priority support&#10;- Training videos"
                className="bg-white/5 border-white/10 text-white min-h-[150px]"
              />
            </div>
          </div>
        );
        
      case "riskReversal":
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <label className="text-sm font-medium text-white/80 font-mono">Guarantee Type</label>
              <select
                value={inputs.guaranteeType || "performance"}
                onChange={(e) => updatePhaseInput(phase, "guaranteeType", e.target.value)}
                className="w-full h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-white focus:border-[#00d4aa] focus:outline-none"
              >
                {GUARANTEE_TYPES.map(type => (
                  <option key={type.value} value={type.value} className="bg-[#1a1a2e]">
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-white/80 font-mono">Guarantee Conditions</label>
              <Textarea
                value={inputs.guaranteeConditions || ""}
                onChange={(e) => updatePhaseInput(phase, "guaranteeConditions", e.target.value)}
                placeholder="Specific terms of the guarantee:&#10;- What triggers the guarantee?&#10;- What do they get if conditions aren't met?&#10;- Any requirements on their part?"
                className="bg-white/5 border-white/10 text-white min-h-[150px]"
              />
            </div>
          </div>
        );
        
      case "pricingPosition":
        return (
          <div className="space-y-6">
            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-white/80 font-mono">Delivery Cost</label>
                <Input
                  value={inputs.deliveryCost || ""}
                  onChange={(e) => updatePhaseInput(phase, "deliveryCost", e.target.value)}
                  placeholder="e.g., $2,000"
                  className="bg-white/5 border-white/10 text-white"
                />
                <p className="text-xs text-white/40">Your cost to deliver</p>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-white/80 font-mono">Problem Cost</label>
                <Input
                  value={inputs.problemCost || ""}
                  onChange={(e) => updatePhaseInput(phase, "problemCost", e.target.value)}
                  placeholder="e.g., $50,000/year"
                  className="bg-white/5 border-white/10 text-white"
                />
                <p className="text-xs text-white/40">Cost of NOT solving</p>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-white/80 font-mono">Proposed Price</label>
                <Input
                  value={inputs.proposedPrice || ""}
                  onChange={(e) => updatePhaseInput(phase, "proposedPrice", e.target.value)}
                  placeholder="e.g., $5,000"
                  className="bg-white/5 border-white/10 text-white"
                />
                <p className="text-xs text-white/40">Your price point</p>
              </div>
            </div>
          </div>
        );
        
      case "urgencyLayer":
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <label className="text-sm font-medium text-white/80 font-mono">Urgency Reason</label>
              <Textarea
                value={inputs.urgencyReason || ""}
                onChange={(e) => updatePhaseInput(phase, "urgencyReason", e.target.value)}
                placeholder="Legitimate reason for the deadline:&#10;- Limited spots available&#10;- Seasonal opportunity&#10;- Price increase coming"
                className="bg-white/5 border-white/10 text-white min-h-[120px]"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-white/80 font-mono">Deadline</label>
              <Input
                value={inputs.deadline || ""}
                onChange={(e) => updatePhaseInput(phase, "deadline", e.target.value)}
                placeholder="e.g., Friday 5pm AWST"
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
          </div>
        );
        
      case "finalOffer":
        return (
          <div className="space-y-6">
            {/* Summary Grid */}
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="bg-white/5 border-white/10">
                <CardContent className="pt-4">
                  <p className="text-xs font-mono text-white/40 mb-1">CLIENT</p>
                  <p className="text-white font-medium">{offer.clientName || "Not set"}</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white/5 border-white/10">
                <CardContent className="pt-4">
                  <p className="text-xs font-mono text-white/40 mb-1">INDUSTRY</p>
                  <p className="text-white font-medium">{offer.industry || "Not set"}</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white/5 border-white/10">
                <CardContent className="pt-4">
                  <p className="text-xs font-mono text-white/40 mb-1">AUDIENCE</p>
                  <p className="text-white font-medium">
                    {offer.phases.marketResearch?.inputs?.audienceSegment || "Not set"}
                  </p>
                </CardContent>
              </Card>
              
              <Card className="bg-white/5 border-white/10">
                <CardContent className="pt-4">
                  <p className="text-xs font-mono text-white/40 mb-1">CORE OUTCOME</p>
                  <p className="text-white font-medium">
                    {offer.phases.offerArchitecture?.inputs?.coreOutcome || "Not set"}
                  </p>
                </CardContent>
              </Card>
              
              <Card className="bg-white/5 border-white/10">
                <CardContent className="pt-4">
                  <p className="text-xs font-mono text-white/40 mb-1">GUARANTEE</p>
                  <p className="text-white font-medium capitalize">
                    {offer.phases.riskReversal?.inputs?.guaranteeType || "Not set"}
                  </p>
                </CardContent>
              </Card>
              
              <Card className="bg-white/5 border-white/10">
                <CardContent className="pt-4">
                  <p className="text-xs font-mono text-white/40 mb-1">PRICE</p>
                  <p className="text-white font-medium">
                    {offer.phases.pricingPosition?.inputs?.proposedPrice || "Not set"}
                  </p>
                </CardContent>
              </Card>
            </div>
            
            <div className="p-4 bg-[#00d4aa]/10 border border-[#00d4aa]/30 rounded-lg">
              <p className="text-sm text-[#00d4aa]">
                Click &quot;Generate Insights&quot; to create the complete offer copy for ads, landing pages, and sales scripts.
              </p>
            </div>

            {/* Handoff: turn a completed offer into a campaign brief. */}
            {offer.phases.finalOffer?.aiOutput && (
              <div className="p-5 bg-gradient-to-br from-[#00d4aa]/15 to-purple-500/10 border border-[#00d4aa]/40 rounded-xl">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-[#00d4aa]/20">
                    <ArrowRight className="w-5 h-5 text-[#00d4aa]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-semibold mb-1">Ready to launch?</h3>
                    <p className="text-white/60 text-sm mb-4">
                      Spin up a campaign brief pre-filled from this offer — no blank form.
                    </p>
                    <Button
                      onClick={createCampaignFromOffer}
                      className="bg-[#00d4aa] hover:bg-[#00e6bb] text-black font-semibold"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Create Campaign from this Offer
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
        
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[#00d4aa]" />
      </div>
    );
  }

  const currentPhaseData = offer.phases[PHASES[currentPhase].id];
  const PhaseIcon = PHASES[currentPhase].icon;

  return (
    <div className="animate-fadeIn">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">
            {offer.clientName || "New Offer"}
          </h1>
          <p className="text-white/60 text-sm mt-1">
            {lastSaved ? `Last saved ${lastSaved.toLocaleTimeString()}` : "Not saved yet"}
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            onClick={copyAllContent}
            className="text-white/60 hover:text-white hover:bg-white/10"
          >
            <Copy className="w-4 h-4 mr-2" />
            Copy All
          </Button>
          
          <Button
            onClick={() => saveOffer(false)}
            disabled={isSaving}
            className="bg-[#00d4aa] hover:bg-[#00e6bb] text-black font-semibold"
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Offer
          </Button>
        </div>
      </div>
      
      {/* Phase Navigation */}
      <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
        {PHASES.map((phase, index) => {
          const Icon = phase.icon;
          const isActive = currentPhase === index;
          const isCompleted = offer.phases[phase.id]?.aiOutput;
          
          return (
            <button
              key={phase.id}
              onClick={() => setCurrentPhase(index)}
              className={`flex items-center gap-2 px-4 py-3 rounded-xl whitespace-nowrap transition-all ${
                isActive
                  ? "bg-[rgba(0,212,170,0.15)] border-2 border-[rgba(0,212,170,0.4)]"
                  : "bg-white/5 border-2 border-transparent hover:bg-white/10"
              }`}
            >
              <div
                className={`p-1.5 rounded-lg ${isActive ? "bg-[#00d4aa]/20" : "bg-white/10"}`}
                style={{ color: isActive ? phase.color : "rgba(255,255,255,0.6)" }}
              >
                <Icon className="w-4 h-4" />
              </div>
              <span className={`text-sm font-medium ${isActive ? "text-white" : "text-white/60"}`}>
                {phase.name}
              </span>
              {isCompleted && (
                <CheckCircle className="w-4 h-4 text-[#00d4aa]" />
              )}
            </button>
          );
        })}
      </div>
      
      {/* Main Content Grid */}
      <div className={`grid gap-6 ${showOutput ? "lg:grid-cols-2" : "grid-cols-1"}`}>
        {/* Form Panel */}
        <Card className="card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div
                className="p-2 rounded-lg"
                style={{ backgroundColor: `${PHASES[currentPhase].color}20` }}
              >
                <PhaseIcon className="w-5 h-5" style={{ color: PHASES[currentPhase].color }} />
              </div>
              <div>
                <CardTitle className="text-white">{PHASES[currentPhase].name}</CardTitle>
                <CardDescription className="text-white/60">
                  Phase {currentPhase + 1} of {PHASES.length}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {renderPhaseForm()}
            
            {/* Phase Actions */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/10">
              <Button
                variant="ghost"
                onClick={() => setCurrentPhase(Math.max(0, currentPhase - 1))}
                disabled={currentPhase === 0}
                className="text-white/60 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Previous
              </Button>
              
              <Button
                onClick={generateInsight}
                disabled={isGenerating}
                className="bg-[#00d4aa] hover:bg-[#00e6bb] text-black font-semibold px-6"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate Insights
                  </>
                )}
              </Button>
              
              <Button
                variant="ghost"
                onClick={() => setCurrentPhase(Math.min(PHASES.length - 1, currentPhase + 1))}
                disabled={currentPhase === PHASES.length - 1}
                className="text-white/60 hover:text-white"
              >
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
        
        {/* Output Panel */}
        {showOutput && (
          <Card className="card bg-[#0d0d14]">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#00d4aa]" />
                  AI Output
                </CardTitle>
                {currentPhaseData?.aiOutput && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => copyToClipboard(currentPhaseData.aiOutput)}
                    className="text-[#00d4aa] hover:bg-[#00d4aa]/10"
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    Copy
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {currentPhaseData?.aiOutput ? (
                <div className="bg-[rgba(24,24,27,1)] rounded-lg p-4 max-h-[60vh] overflow-y-auto">
                  <pre className="text-white/80 text-sm font-mono whitespace-pre-wrap leading-7">
                    {currentPhaseData.aiOutput}
                  </pre>
                </div>
              ) : (
                <div className="bg-[rgba(24,24,27,1)] rounded-lg p-8 text-center">
                  <Sparkles className="w-12 h-12 text-white/20 mx-auto mb-4" />
                  <p className="text-white/40 text-sm">
                    Fill in the form and click &quot;Generate Insights&quot; to get AI-powered recommendations for this phase.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
      
      {/* Mobile Output Toggle */}
      <div className="lg:hidden fixed bottom-4 right-4">
        <Button
          onClick={() => setShowOutput(!showOutput)}
          className="bg-[#00d4aa] hover:bg-[#00e6bb] text-black shadow-lg"
        >
          <FileText className="w-4 h-4 mr-2" />
          {showOutput ? "Hide Output" : "Show Output"}
        </Button>
      </div>
    </div>
  );
}

export default OfferBuilder;
